import { useMapContext } from "@/hooks/useMap";
import { useBasemaps } from "@/hooks/useBasemaps";
import { TbCheck, TbMapPin, TbMapPinOff, TbX } from "react-icons/tb";

export const LayersSwitcher: React.FC = () => {
  const {
    showBasemapSwitcher,
    setShowBasemapSwitcher,
    taskLayerVisible,
    setTaskLayerVisible,
    toggleArcgisLayer,
    getVisibleArcgisLayers,
    selectedBasemapKey,
    setSelectedBasemapKey,
  } = useMapContext();

  // Load basemaps from database
  const { visibleBasemaps } = useBasemaps();

  if (!showBasemapSwitcher) return null;

  const layersWithVisibility = getVisibleArcgisLayers();
  const selectedBasemap =
    visibleBasemaps.find((basemap) => basemap.key === selectedBasemapKey) ??
    visibleBasemaps[0];

  const handleBaseMapChange = (basemapKey: string) => {
    setSelectedBasemapKey(basemapKey);
  };

  return (
    <div className="absolute top-20 right-4 z-2001 bg-white rounded-[20px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.08)] min-w-[180px] max-h-[calc(100vh-100px)] overflow-y-auto">
      {/* Base maps section */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="font-['Open_Sans'] font-bold text-[12px] text-[#828282] uppercase leading-normal">
            Base maps
          </div>
          {/* Close button */}
          <button
            onClick={() => setShowBasemapSwitcher(false)}
            className="cursor-pointer z-2002 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            aria-label="Close layers switcher"
          >
            <TbX className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          {visibleBasemaps.map((opt) => {
            const isSelected = selectedBasemap?.key === opt.key;
            return (
              <button
                key={opt.key}
                className={`flex items-center gap-2.5 px-4 py-3.5 rounded-[10px] border border-gray-100 transition-colors ${
                  isSelected
                    ? "bg-[rgba(55,71,149,0.04)]"
                    : "bg-white hover:bg-gray-50"
                }`}
                onClick={() => handleBaseMapChange(opt.key)}
                aria-pressed={isSelected}
              >
                <div
                  className={`w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center ${
                    isSelected
                      ? "bg-[#374795] border-[#374795]"
                      : "border-[#bfbfbf]"
                  }`}
                >
                  {isSelected && <TbCheck className="w-3.5 h-3.5 text-white" />}
                </div>
                <span className="font-['Open_Sans'] font-semibold text-[12px] text-[#444444] leading-[1.4]">
                  {opt.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Divider line */}
      <div className="h-px bg-gray-200 mx-4"></div>

      {/* Data layers section */}
      <div className="p-4">
        <div className="font-['Open_Sans'] font-bold text-[12px] text-[#828282] uppercase leading-normal mb-2">
          Data Layers
        </div>
        <div className="flex flex-col gap-2">
          {layersWithVisibility.map((layer) => (
            <button
              key={layer.key}
              className="flex items-center gap-2.5 px-4 py-3.5 rounded-[10px] border border-gray-100 bg-white hover:bg-gray-50 transition-colors"
              onClick={() => toggleArcgisLayer(layer.key)}
              aria-pressed={layer.visible}
            >
              <div
                className={`w-[18px] h-[18px] rounded-[5px] border-[1.5px] ${
                  layer.visible
                    ? "bg-[#374795] border-[#374795]"
                    : "border-[#bfbfbf]"
                }`}
              >
                {layer.visible && (
                  <TbCheck className="w-3.5 h-3.5 text-white" />
                )}
              </div>
              <span className="font-['Open_Sans'] font-semibold text-[12px] text-[#444444] leading-[1.4]">
                {layer.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Divider line */}
      <div className="h-px bg-gray-200 mx-4"></div>

      {/* Task layer section */}
      <div className="p-4">
        <div className="font-['Open_Sans'] font-bold text-[12px] text-[#828282] uppercase leading-normal mb-2">
          Task Layer
        </div>
        <button
          className="flex items-center gap-2.5 px-4 py-3.5 rounded-[10px] border border-gray-100 bg-white hover:bg-gray-50 transition-colors w-full"
          onClick={() => setTaskLayerVisible((v: boolean) => !v)}
          aria-pressed={taskLayerVisible}
        >
          <div className="flex items-center justify-center w-[18px] h-[18px]">
            {taskLayerVisible ? (
              <TbMapPin className="w-4 h-4 text-[#374795]" />
            ) : (
              <TbMapPinOff className="w-4 h-4 text-[#828282]" />
            )}
          </div>
          <span className="font-['Open_Sans'] font-semibold text-[12px] text-[#444444] leading-[1.4]">
            {taskLayerVisible ? "Hide Task Layer" : "Show Task Layer"}
          </span>
        </button>
      </div>
    </div>
  );
};
